import { X, Plus, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import IconButton from "@/shared/components/ui/Button/IconButton";

const cn = (...inputs) => twMerge(clsx(inputs));

// Email validation helper function
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Styles '(Recommended)' text dynamically inside option labels
const renderLabelWithRecommendation = (labelText) => {
  if (typeof labelText !== "string") return labelText;

  const recIndex = labelText.indexOf("(Recommended)");
  if (recIndex === -1) return labelText;

  const before = labelText.substring(0, recIndex);
  const after = labelText.substring(recIndex + "(Recommended)".length);

  return (
    <>
      {before}
      <span className="text-secondary font-semibold">(Recommended)</span>
      {after}
    </>
  );
};

export function ChipInput({
  defaultChips = [],
  placeholder,
  secondaryPlaceholder,
  dropdownOptions = [],
  onChange,
  className = "",
  errors = null,
  px,
  py,
  minHeight,
  type,
  classNameChips = "",
  disabled = false,
  maxChips = null,
  allowCustomChips = true,
  showOptionDescription = false,
  showChipTooltip = false,
  onCreateCustomChip = null,
}) {
  // Initialize chips state with default values
  const [chips, setChips] = useState(() => defaultChips || []);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [validationError, setValidationError] = useState("");

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const dropdownPortalRef = useRef(null);
  const createPanelRef = useRef(null);

  const [dropdownLayout, setDropdownLayout] = useState(null);

  // Track if we're syncing from defaultChips to prevent onChange loops
  const isSyncingFromDefaultRef = useRef(false);

  // Use ref to track previous defaultChips
  const prevDefaultChipsStrRef = useRef(JSON.stringify(defaultChips || []));

  useEffect(() => {
    const currentStr = JSON.stringify(defaultChips || []);
    const prevStr = prevDefaultChipsStrRef.current;

    if (currentStr !== prevStr) {
      prevDefaultChipsStrRef.current = currentStr;

      // Mark that we're syncing from defaultChips
      isSyncingFromDefaultRef.current = true;

      setChips((currentChips) => {
        const currentChipsStr = JSON.stringify(currentChips || []);

        // Only update if current chips are different from new defaultChips
        if (currentChipsStr !== currentStr) {
          if (defaultChips && Array.isArray(defaultChips)) {
            return defaultChips;
          }
          return [];
        }

        return currentChips;
      });

      // Reset sync flag after state update
      setTimeout(() => {
        isSyncingFromDefaultRef.current = false;
      }, 0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultChips]);

  // update chips array
  const updateChips = (newChips) => {
    setChips(newChips);

    if (!isSyncingFromDefaultRef.current) {
      onChange?.(newChips);
    }
  };

  const normalizeOption = (option) => {
    if (option && typeof option === "object") {
      return {
        label: String(option.label ?? option.title ?? option.value ?? ""),
        value: String(option.value ?? option.key ?? option.label ?? ""),
        description: String(option.description ?? ""),
      };
    }

    return {
      label: String(option ?? ""),
      value: String(option ?? ""),
      description: "",
    };
  };

  const normalizedDropdownOptions = dropdownOptions.map(normalizeOption);

  const getOptionForValue = (chipValue) =>
    normalizedDropdownOptions.find(
      (o) => o.value.toLowerCase() === String(chipValue).trim().toLowerCase(),
    );

  const getChipLabel = (chipValue) =>
    getOptionForValue(chipValue)?.label ?? String(chipValue);

  /** True if input matches an existing chip or dropdown row ignoring case. */
  const matchesExistingCaseInsensitive = (raw) => {
    const t = String(raw ?? "").trim();

    if (!t) return true;

    const lower = t.toLowerCase();

    if (chips.some((c) => String(c).trim().toLowerCase() === lower)) {
      return true;
    }

    return normalizedDropdownOptions.some(
      (o) => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower,
    );
  };

  /** When input matches a dropdown option case-insensitively, use that option's stored value. */
  const resolveChipValue = (raw) => {
    const trimmed = String(raw ?? "").trim();

    if (!trimmed || normalizedDropdownOptions.length === 0) {
      return trimmed;
    }

    const lower = trimmed.toLowerCase();

    const match = normalizedDropdownOptions.find(
      (o) => o.value.toLowerCase() === lower || o.label.toLowerCase() === lower,
    );

    return match ? match.value : trimmed;
  };

  // add chips with validation
  const addChip = (chip) => {
    if (disabled) return;

    const trimmed = chip.trim();

    if (!trimmed) return;

    const resolved = resolveChipValue(trimmed);
    const matchedOption = getOptionForValue(resolved);

    if (
      !allowCustomChips &&
      normalizedDropdownOptions.length > 0 &&
      !matchedOption
    ) {
      setValidationError("Choose from the list");
      setTimeout(() => setValidationError(""), 2000);
      return;
    }

    if (!matchedOption && onCreateCustomChip) {
      onCreateCustomChip(trimmed);
      setInputValue("");
      setShowDropdown(false);
      return;
    }

    if (maxChips != null && chips.length >= maxChips) {
      setValidationError(`Maximum ${maxChips} allowed`);
      setTimeout(() => setValidationError(""), 2000);
      return;
    }

    // Check for duplicates (case-insensitive)
    if (
      chips.some(
        (c) => String(c).trim().toLowerCase() === resolved.toLowerCase(),
      )
    ) {
      setValidationError("This item already exists");

      setTimeout(() => setValidationError(""), 2000);

      return;
    }

    // Validate email if type is email
    if (type === "email" && !isValidEmail(trimmed)) {
      setValidationError("Please enter a valid email address");

      setTimeout(() => setValidationError(""), 2000);

      return;
    }

    // Clear any validation errors
    setValidationError("");

    const updated = [...chips, resolved];

    updateChips(updated);
  };

  // remove chip
  const removeChip = (chip) => {
    if (disabled) return;

    const updated = chips.filter((c) => c !== chip);

    updateChips(updated);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;

    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();

      addChip(inputValue);

      if (!validationError) {
        setInputValue("");
      }
    }
  };

  // Separate selected and unselected options
  const unselectedOptions = normalizedDropdownOptions.filter((opt) => {
    if (!opt.value) return false;
    if (
      chips.some(
        (c) => String(c).trim().toLowerCase() === opt.value.toLowerCase(),
      )
    ) {
      return false;
    }
    const query = inputValue.trim().toLowerCase();
    if (!query) return true;
    return (
      opt.label.toLowerCase().includes(query) ||
      (showOptionDescription && opt.description.toLowerCase().includes(query))
    );
  });

  const showCreateSuggestion =
    allowCustomChips &&
    !disabled &&
    inputValue.trim() !== "" &&
    !matchesExistingCaseInsensitive(inputValue.trim()) &&
    (maxChips == null || chips.length < maxChips);

  const visibleChips = expanded ? chips : chips.slice(0, 10);

  const hiddenCount = chips.length - 10;

  const atMaxChips = maxChips != null && chips.length >= maxChips;

  const showOptionsPortal =
    !disabled &&
    !atMaxChips &&
    showDropdown &&
    dropdownOptions.length > 0 &&
    unselectedOptions.length > 0;

  /** Fixed overlay position — escapes accordion overflow:hidden / max-h clipping. */
  useLayoutEffect(() => {
    if (!showOptionsPortal) {
      setDropdownLayout(null);

      return undefined;
    }

    const pad = 8;
    const gap = 6;

    const updateLayout = () => {
      const el = containerRef.current;

      if (!el) return;

      const rect = el.getBoundingClientRect();

      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      let left = rect.left;
      let width = rect.width;

      if (left < pad) {
        width -= pad - left;
        left = pad;
      }

      if (left + width > viewportW - pad) {
        width = Math.max(160, viewportW - pad - left);
      }

      const spaceBelow = viewportH - rect.bottom - gap - pad;

      const maxHeight = Math.max(160, spaceBelow);

      setDropdownLayout({
        top: rect.bottom + gap,
        left,
        width,
        maxHeight,
      });
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);
    window.addEventListener("scroll", updateLayout, true);

    return () => {
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("scroll", updateLayout, true);
    };
  }, [showOptionsPortal, chips, inputValue, dropdownOptions.length]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const inContainer = containerRef.current?.contains(event.target);

      const inPortal = dropdownPortalRef.current?.contains(event.target);

      if (!inContainer && !inPortal) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Create panel is absolutely positioned (no layout height), so it often sits below scroll parents — scroll it into view. */
  useEffect(() => {
    if (!showCreateSuggestion) return;

    let cancelled = false;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;

        createPanelRef.current?.scrollIntoView({
          block: "nearest",
          inline: "nearest",
          behavior: "auto",
        });
      });
    });

    return () => {
      cancelled = true;
    };
  }, [showCreateSuggestion, inputValue]);

  const toggleChipsExpansion = () => {
    if (disabled) return;

    if (chips.length <= 10) {
      setShowDropdown(!showDropdown);
    } else {
      setExpanded(!expanded);
    }
  };

  /** Focus alone does not re-fire `onFocus` when already focused — reopen list on click. */
  const activateField = () => {
    if (disabled || atMaxChips) return;

    inputRef.current?.focus();

    if (dropdownOptions.length > 0) {
      setShowDropdown(true);
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <div
        className={cn(
          `border w-full rounded-lg ${
            px ? px : " px-3"
          } ${py ? py : "py-3"} flex flex-nowrap items-start gap-2 ${
            minHeight ? minHeight : "min-h-[50px]"
          } ${
            disabled
              ? "bg-gray-100 dark:bg-gray-700 cursor-not-allowed opacity-60"
              : "bg-gray-50 dark:bg-gray-800"
          } ${
            errors || validationError
              ? "border-red-500"
              : "border-gray-300 dark:border-gray-700 focus-within:border-secondary focus-within:ring-1 focus-within:ring-secondary"
          } justify-between`,
          className,
        )}
        onClick={activateField}
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2">
          {chips.length > 0 ? (
            <div className="flex max-h-[min(220px,45vh)] min-h-0 flex-wrap items-center gap-2 overflow-y-auto [scrollbar-gutter:stable]">
              {visibleChips.map((chip, index) => {
                const option = getOptionForValue(chip);
                const chipLabel = getChipLabel(chip);
                const chipBody = (
                  <span className="inline-flex cursor-default">
                    {renderLabelWithRecommendation(chipLabel)}
                  </span>
                );

                return (
                  <div
                    key={index}
                    className={cn(
                      "inline-flex items-center gap-[8px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 px-2.5 py-[8px] text-[13px] font-normal leading-5 text-gray-900 dark:text-gray-100 font-['Inter']",
                      classNameChips,
                    )}
                  >
                    {showChipTooltip && option?.description ? (
                      <CustomCommonTooltip
                        placement="top"
                        showArrow
                        content={
                          <div className="space-y-2">
                            <div className="font-semibold text-[13px] text-gray-900 dark:text-gray-100 text-wrap break-words leading-snug">
                              {chipLabel}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-300 text-wrap break-words leading-relaxed">
                              {option.description}
                            </div>
                          </div>
                        }
                      >
                        {chipBody}
                      </CustomCommonTooltip>
                    ) : (
                      renderLabelWithRecommendation(chipLabel)
                    )}

                    <IconButton
                      ariaLabel={`Remove ${chipLabel}`}
                      className="h-5 min-w-5 w-5 shrink-0 hover:text-red-500"
                      disabled={disabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        removeChip(chip);
                      }}
                    ><X size={12} strokeWidth={2.5} /></IconButton>
                  </div>
                );
              })}

              {!expanded && hiddenCount > 0 && (
                <div
                  className="bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-300 text-[13px] border border-gray-200 dark:border-gray-700 font-medium px-3 py-2 rounded-full cursor-pointer flex items-center"
                  onClick={(e) => {
                    if (disabled) return;

                    e.stopPropagation();

                    setExpanded(true);
                  }}
                >
                  <Plus className="w-3 h-3" strokeWidth={2.5} />

                  {hiddenCount}
                </div>
              )}
            </div>
          ) : null}

          <input
            ref={inputRef}
            type="text"
            aria-label={placeholder || secondaryPlaceholder || "Add item"}
            disabled={disabled || atMaxChips}
            value={inputValue}
            onChange={(e) => {
              if (disabled || atMaxChips) return;

              setInputValue(e.target.value);
              setShowDropdown(true);
              setValidationError("");
            }}
            onFocus={() => {
              if (disabled || atMaxChips) return;

              setShowDropdown(true);
            }}
            onClick={() => {
              if (disabled || atMaxChips) return;

              if (dropdownOptions.length > 0) {
                setShowDropdown(true);
              }
            }}
            onKeyDown={handleKeyDown}
            className={`h-6 shrink-0 text-sm font-medium leading-6 text-gray-900 dark:text-gray-100 bg-transparent py-0 outline-none disabled:cursor-not-allowed placeholder:text-gray-500 dark:placeholder:text-gray-400 placeholder:font-normal placeholder:text-xs ${
              chips.length > 0 ? "w-full min-w-0" : "min-w-[4rem] w-full flex-1"
            }`}
            placeholder={
              atMaxChips && maxChips != null
                ? `Maximum ${maxChips} selected`
                : chips.length > 0
                  ? secondaryPlaceholder
                  : placeholder
            }
          />
        </div>

        {(dropdownOptions.length > 0 || chips.length > 10) && (
          <div className="flex h-6 shrink-0 items-center">
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();

                toggleChipsExpansion();
              }}
              className={cn(
                "ml-1",
                disabled ? "text-gray-300 cursor-not-allowed" : "text-gray-400",
              )}
            >
              {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>
          </div>
        )}
      </div>

      {/* Validation error message */}
      {validationError && (
        <p className="text-red-500 text-xs mt-1 ml-1">{validationError}</p>
      )}

      {showOptionsPortal && dropdownLayout
        ? createPortal(
            <ul
              ref={dropdownPortalRef}
              role="listbox"
              style={{
                position: "fixed",
                top: dropdownLayout.top,
                left: dropdownLayout.left,
                width: dropdownLayout.width,
                maxHeight: dropdownLayout.maxHeight,
                zIndex: 2147483646,
              }}
              className="box-border overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1 shadow-lg"
            >
              {unselectedOptions.map((option, index) => (
                <li
                  key={`unselected-${option.value}-${index}`}
                  role="option"
                  aria-selected={false}
                  tabIndex={0}
                  className="cursor-pointer px-3 py-2.5 text-gray-800 dark:text-gray-100 hover:bg-[#207C9733] hover:text-secondary"
                  onMouseDown={(e) => {
                    if (disabled) return;

                    e.preventDefault();
                    e.stopPropagation();

                    addChip(option.value);

                    if (!validationError) {
                      setInputValue("");
                    }

                    setShowDropdown(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      addChip(option.value);
                      setInputValue("");
                      setShowDropdown(false);
                      inputRef.current?.focus();
                    } else if (e.key === "Escape") {
                      setShowDropdown(false);
                      inputRef.current?.focus();
                    }
                  }}
                >
                  {showOptionDescription && option.description ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[0.815rem] font-normal text-gray-800 dark:text-gray-100">
                        {renderLabelWithRecommendation(option.label)}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {option.description}
                      </span>
                    </div>
                  ) : (
                    <span className="text-[0.815rem] font-normal text-gray-800 dark:text-gray-100">
                      {renderLabelWithRecommendation(option.label)}
                    </span>
                  )}
                </li>
              ))}
            </ul>,
            document.body,
          )
        : null}

      {/* Suggestion chips */}
      {showCreateSuggestion && (
        <div
          ref={createPanelRef}
          className="relative z-[9999999999] mt-1 w-full overflow-x-auto whitespace-nowrap scrollbar-hide"
        >
          <div className="flex w-full flex-col items-start gap-1 !rounded-xl border border-[#E9EAEB] dark:border-gray-700 bg-white dark:bg-gray-800 shadow-[0_4px_8px_0_rgba(54,53,53,0.05)]">
            <div
              className="flex items-center gap-1 self-stretch px-4 py-3 cursor-pointer rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700"
              onClick={(e) => {
                if (disabled) return;

                e.preventDefault();
                e.stopPropagation();

                addChip(inputValue.trim());

                setInputValue("");
              }}
            >
              <Plus className="w-4 h-4 text-secondary" strokeWidth={2.5} />

              <span className="text-secondary font-medium text-sm">Create</span>

              <span className="text-black dark:text-gray-100 font-normal text-sm">
                &quot;{inputValue.trim()}&quot;
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
